import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { usePricingStore } from '../store';
import { COMMODITIES, REFRESH_INTERVALS } from '@jrd/core';
import type { Commodity } from '@jrd/core';

// Mock data for development - replace with real API call
const MOCK_COMMODITIES: Commodity[] = [
  { symbol: 'CL=F', name: 'WTI Crude', price: 68.42, change: 0.23, changePercent: 0.34, updatedAt: new Date() },
  { symbol: 'BZ=F', name: 'Brent Crude', price: 72.18, change: -0.08, changePercent: -0.11, updatedAt: new Date() },
  { symbol: 'RB=F', name: 'RBOB Gasoline', price: 2.12, change: 0.025, changePercent: 1.19, updatedAt: new Date() },
  { symbol: 'HO=F', name: 'Heating Oil', price: 2.45, change: 0.012, changePercent: 0.49, updatedAt: new Date() },
  { symbol: 'NG=F', name: 'Natural Gas', price: 2.89, change: -0.062, changePercent: -2.10, updatedAt: new Date() },
];

function formatPrice(price: number): string {
  return price < 10 ? price.toFixed(3) : price.toFixed(2);
}

function formatPercent(percent: number): string {
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(2)}%`;
}

function CommodityItem({ commodity }: { commodity: Commodity }) {
  const isPositive = commodity.change > 0;
  const isNegative = commodity.change < 0;
  const displayName = COMMODITIES.find(c => c.symbol === commodity.symbol)?.displayName || commodity.name;

  return (
    <div className="inline-flex items-center gap-2 px-4 py-1 border-r border-text-muted/20 last:border-0">
      <span className="text-text-secondary text-xs font-bold uppercase tracking-wide">
        {displayName}
      </span>
      <span className="text-text-primary font-mono text-sm">
        ${formatPrice(commodity.price)}
      </span>
      <span
        className={`flex items-center gap-1 text-xs font-mono ${
          isPositive ? 'text-green-400' : isNegative ? 'text-red-400' : 'text-text-muted'
        }`}
      >
        {isPositive ? (
          <TrendingUp className="w-3 h-3" />
        ) : isNegative ? (
          <TrendingDown className="w-3 h-3" />
        ) : (
          <Minus className="w-3 h-3" />
        )}
        {formatPercent(commodity.changePercent)}
      </span>
    </div>
  );
}

export function CommoditiesTicker() {
  const { commodities, setCommodities, commoditiesLastUpdated } = usePricingStore();
  const [isLoading, setIsLoading] = useState(false);

  // Use mock data initially, replace with API call
  useEffect(() => {
    if (commodities.length === 0) {
      setCommodities(MOCK_COMMODITIES);
    }
  }, [commodities.length, setCommodities]);

  // Refresh interval
  useEffect(() => {
    const fetchCommodities = async () => {
      setIsLoading(true);
      try {
        // TODO: Replace with actual API call
        // const response = await api.getCommodities();
        // if (response.success && response.data) {
        //   setCommodities(response.data);
        // }

        // For now, add small random variations to mock data
        const updatedCommodities = MOCK_COMMODITIES.map(c => ({
          ...c,
          price: c.price + (Math.random() - 0.5) * 0.1,
          change: (Math.random() - 0.5) * 0.2,
          changePercent: (Math.random() - 0.5) * 2,
          updatedAt: new Date(),
        }));
        setCommodities(updatedCommodities);
      } catch (error) {
        console.error('Failed to fetch commodities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const interval = setInterval(fetchCommodities, REFRESH_INTERVALS.commodities);
    return () => clearInterval(interval);
  }, [setCommodities]);

  const displayCommodities = commodities.length > 0 ? commodities : MOCK_COMMODITIES;

  return (
    <div className="w-full bg-bg-darker border-b border-text-muted/20 overflow-hidden">
      <div className="relative flex items-center h-8">
        {/* Status indicator */}
        <div className="absolute left-0 z-10 flex items-center gap-2 px-3 bg-bg-darker">
          <div className={`w-2 h-2 rounded-full ${isLoading ? 'bg-jrd-yellow animate-pulse' : 'bg-jrd-green'}`} />
          <span className="text-text-muted text-[10px] font-mono uppercase tracking-wider">
            COMMODITIES
          </span>
        </div>

        {/* Ticker container */}
        <div className="ticker-container ml-28 flex-1">
          <motion.div
            className="ticker-content"
            animate={{ x: ['0%', '-50%'] }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: 'loop',
                duration: 30,
                ease: 'linear',
              },
            }}
          >
            {/* Double the items for seamless loop */}
            {[...displayCommodities, ...displayCommodities].map((commodity, index) => (
              <CommodityItem key={`${commodity.symbol}-${index}`} commodity={commodity} />
            ))}
          </motion.div>
        </div>

        {/* Last updated */}
        <div className="absolute right-0 z-10 px-3 bg-bg-darker">
          <span className="text-text-muted text-[10px] font-mono">
            {commoditiesLastUpdated
              ? `Updated ${commoditiesLastUpdated.toLocaleTimeString()}`
              : 'Loading...'}
          </span>
        </div>
      </div>
    </div>
  );
}
